<template>
  <BKLoader v-if="!event" />
  <section
    v-else
    class="event"
  >
    <div class="event-container">
      <div class="details">
        <div class="background">
          <v-img
            :src="getImageById(event.image, 800, 800, 'event')"
            height="100%"
            width="100%"
            class="background-image"
          />
          <div class="gradient" />
        </div>
        <div
          :style="`background-image: url(${getImageById(event.image, 1070, 1070, 'event')})`"
          class="image"
        />

        <EventSidebar
          :event.sync="event"
          @reload:event="initEvent"
        />
      </div>
      <template
        v-if="event.isFinished && (event.permissions.canEdit || event.permissions.canLeaveFeedback || event?.feedback)"
      >
        <div class="tabContainer">
          <button
            class="tabItem"
            :class="{ active: tabSelected === 'info' }"
            @click="tabSelected = 'info'"
          >
            Info
          </button>
          <button
            class="tabItem"
            :class="{ active: tabSelected === 'feedback' }"
            @click="tabSelected = 'feedback'"
          >
            Feedback <span v-if="event.permissions.canEdit">({{ event.score.countFeedbacks }} reviews)</span>
          </button>
        </div>
        <div
          v-if="tabSelected === 'info'"
          class="information"
        >
          <EventPurchasedTickets
            v-if="event.paymentSystem?.tickets?.length"
            :tickets="event.paymentSystem.tickets"
            :event-id="event.id"
            class="event-tickets"
          />
          <div class="desc">
            <h2
              v-if="event.battles && event.battles.length"
              class="information-title"
            >
              Battles
            </h2>
            <!-- Battles container -->
            <EventBattles :event="event" />

            <h2
              v-if="event.description"
              class="information-title"
            >
              Description
            </h2>
            <!--          eslint-disable-next-line-->
            <p
              class="information-description"
              v-html="instance?.proxy?.$sanitize(event.description)"
            />
            <MediaPanel
              :can-edit="event.permissions.canEdit"
              :event-id="event.id"
            />
          </div>
          <EventStaff
            :event="event"
            class="event-staff"
          />
        </div>
        <template v-if="tabSelected === 'feedback'">
          <template v-if="event.permissions.canEdit && feedbacks">
            <EventUserFeedback
              v-for="(feedback, index) in feedbacks.feedbacks"
              :key="feedback.id"
              :feedback="feedback"
              :with-line="index !== (feedbacks.feedbacks?.length ?? 0) - 1"
            />
          </template>
          <template v-else-if="event.permissions.canLeaveFeedback || event?.feedback">
            <EventFeedbackForm
              :set-feedback="() => initEvent()"
              :event="event"
              :feedback="event.feedback"
            />
          </template>
          <template v-else>
            <p>Sorry, you don't have permission to view feedback for this event.</p>
          </template>
        </template>
      </template>
      <template v-else>
        <div class="information">
          <EventPurchasedTickets
            v-if="event.paymentSystem?.tickets?.length"
            :tickets="event.paymentSystem.tickets"
            :event-id="event.id"
            class="event-tickets"
          />
          <div class="desc">
            <h2
              v-if="event.battles && event.battles.length"
              class="information-title"
            >
              Battles
            </h2>
            <!-- Battles container -->
            <EventBattles :event="event" />

            <h2
              v-if="event.description"
              class="information-title"
            >
              Description
            </h2>
            <!--          eslint-disable-next-line-->
            <p
              class="information-description"
              v-html="instance?.proxy?.$sanitize(event.description)"
            />
            <MediaPanel
              :can-edit="event.permissions.canEdit"
              :event-id="event.id"
            />
          </div>
          <EventStaff
            :event="event"
            class="event-staff"
          />
        </div>
      </template>

      <BKModal
        :is-show.sync="isShowModal"
        border-width="large"
        class="modal"
        modal-width="500px"
        @update:isShow="onModalClose"
      >
        <template #title>
          <div class="modal-title">
            <BKSvgIcon
              color="var(--color-default-orange)"
              height="60px"
              name="email"
              width="60px"
            />
          </div>
        </template>

        <h3 class="subtitle">We've emailed you some help</h3>
        <p class="description">We've emailed you a QR code for your event and a "How To Guide" for Break Konnect.</p>
      </BKModal>
    </div>
  </section>
</template>

<script lang="ts" setup>
// @ts-expect-error JS file
import store from '@/store'
import { MediaPanel } from '@/widgets/MediaPanel'
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useImage } from '@/shared/composables/image'
import { eventApi, IEvent } from '@/enitites/event'
import { BKModal } from '@/shared/ui/modals'
import BKSvgIcon from '@/components/AppShared/BKSvgIcon.vue'
import { useQuerySync } from '@/composables/useQuerySync'
import EventPurchasedTickets from '@/features/tickets/ui/EventPurchasedTickets.vue'
import { useEventFeedbacksQuery } from '@/enitites/event/api/queries'
import EventBattles from './EventBattles.vue'
import EventStaff from './EventStaff.vue'
import EventSidebar from './EventSidebar.vue'
import EventUserFeedback from './EventUserFeedback.vue'
import EventFeedbackForm from './EventFeedbackForm.vue'

const instance = getCurrentInstance()
const { getImageById } = useImage()
const route = useRoute()
const event = ref<IEvent>()
const eventId = computed(() => route.params.id)
const eventIdNum = computed(() => Number(eventId.value))

const tabSelected = ref<'info' | 'feedback'>('info')
useQuerySync(
  tabSelected,
  'tab',
  (val) => (val === 'feedback' ? 'feedback' : 'info'),
  (val) => val
)

const isFeedbackTab = computed(() => tabSelected.value === 'feedback')

const { data: feedbacks } = useEventFeedbacksQuery(
  eventIdNum.value,
  computed(() => isFeedbackTab.value && !!event.value?.permissions.canEdit)
)

const isShowModal = ref(store.getters.getShowEventInfoModal)

function onModalClose(value: boolean) {
  if (!value) {
    store.dispatch('setShowEventInfoModal', false)
  }
}

async function initEvent() {
  event.value = undefined
  const fetchedEvent = await eventApi.getEvent(Number(eventId.value))
  if (!fetchedEvent) {
    await store.dispatch('setIsNotFound', !fetchedEvent)
    return
  }

  event.value = {
    ...fetchedEvent,
  }
}

watch(eventId, () => {
  initEvent()
})

onMounted(() => {
  initEvent()
})
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.bk-modal {
  .modal-title {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }

  .subtitle {
    @include font-h3;

    margin-bottom: 12px;
    color: var(--color-dark-800);
  }

  .description {
    @include font-body1;

    margin-bottom: 0;
    color: var(--color-dark-800);
  }
}

.event {
  position: relative;
  padding: 80px 0 48px;
  background: var(--color-white);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  @media (max-width: 920px) {
    background: white !important;
  }

  .background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgb(0 0 0 / 70%), rgb(0 0 0 / 50%));
    backdrop-filter: blur(200px);
  }

  .alert {
    position: absolute;
    z-index: 1;
    width: 100%;
  }

  .event-container {
    width: 100%;
    height: 100%;
    min-height: 1000px;
    margin: 0 auto 10px;
    background-color: var(--color-white);

    @media (max-width: 920px) {
      margin: 0 auto;
    }
  }

  .details {
    position: relative;
    display: flex;
    justify-content: center;
    gap: 30px 40px;
    padding: 60px 40px;
    width: 100%;

    > * {
      flex: 0 1 535px;
    }

    @media (max-width: 920px) {
      flex-wrap: wrap;
      padding: 30px 20px;
    }

    .image {
      position: relative;
      align-self: flex-start;
      width: 100%;
      height: auto;
      aspect-ratio: 1 / 1;
      background-position: center;
      background-size: cover;
      border-radius: 6px;
      background-color: var(--color-grey-300);
      box-shadow: 0 8px 20px rgb(0 0 0 / 16%);
    }
  }

  .tabContainer {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    max-width: 865px;
    margin: 40px auto 28px;

    @media (max-width: 920px) {
      width: 90%;
      margin: 40px auto 28px;
    }
  }

  .tabItem {
    @include font-body1;

    flex: 1;
    padding-bottom: 16px;
    color: #666;
    border-bottom: solid 2px #ddd;

    &.active {
      @include font-h5;

      color: #333;
      font-weight: 700;
      border-bottom: solid 2px #333;

      span {
        color: #666;
      }
    }
  }

  .information {
    position: relative;
    display: grid;
    grid-template-areas:
      'tickets tickets'
      'desc    staff';
    grid-template-columns: minmax(auto, 535px) 1fr;
    grid-template-rows: auto 1fr;
    gap: 40px;
    margin: 0 auto;
    padding: 40px;
    width: 100%;
    max-width: 1190px;
    min-height: 780px;
    background: white;

    @media (max-width: 920px) {
      display: flex;
      flex-direction: column;
      padding: 20px;
    }

    .information-title {
      @include font-h4;

      margin-bottom: 8px;
    }
  }

  .desc {
    grid-area: desc;
    width: 100%;
  }

  .event-tickets {
    grid-area: tickets;
  }

  .event-staff {
    grid-area: staff;
  }

  .battles,
  .information-title + p {
    margin-bottom: 20px;
  }
}
</style>
<style lang="scss">
.information-description {
  ul,
  li {
    list-style-type: inherit;
  }

  p {
    margin: 0 !important;
  }
}
</style>
